import React, { useState } from 'react';

// Define types for our component
interface CsvToHtmlProps {}

// CSV to HTML conversion function
const csvToHtml = (
  csv: string, 
  options: {
    header: boolean;
    delimiter: string;
    trimValues: boolean;
    skipEmptyLines: boolean;
    tableClass: string;
    headerClass: string;
    bodyClass: string;
    rowClass: string;
    cellClass: string;
    headerCellClass: string;
    stripedRows: boolean;
    responsiveTable: boolean;
    caption: string;
  }
): string => {
  try {
    // Split the CSV by lines
    const lines = csv.split(/\r?\n/);
    
    // Handle empty CSV
    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return '<p>No data to display</p>';
    }
    
    let htmlTable = '';
    let tableClasses = options.tableClass;
    
    if (options.responsiveTable) {
      // Wrap table in a responsive div if responsive option is enabled
      htmlTable += '<div style="overflow-x: auto;">\n';
      tableClasses += ' ' + (tableClasses ? '' : 'responsive-table');
    }
    
    // Start table with classes
    htmlTable += `<table${tableClasses ? ` class="${tableClasses}"` : ''}>\n`;
    
    // Add caption if provided
    if (options.caption) {
      htmlTable += `  <caption>${escapeHtml(options.caption)}</caption>\n`;
    }
    
    // Process headers if they exist
    let startRow = 0;
    
    if (options.header && lines.length > 0) {
      const headerRow = lines[0];
      const headers = parseCSVLine(headerRow, options.delimiter);
      
      htmlTable += `  <thead${options.headerClass ? ` class="${options.headerClass}"` : ''}>\n`;
      htmlTable += `    <tr${options.rowClass ? ` class="${options.rowClass}"` : ''}>\n`;
      
      for (const header of headers) {
        const headerValue = options.trimValues ? header.trim() : header;
        htmlTable += `      <th${options.headerCellClass ? ` class="${options.headerCellClass}"` : ''}>${escapeHtml(headerValue)}</th>\n`;
      }
      
      htmlTable += '    </tr>\n';
      htmlTable += '  </thead>\n';
      startRow = 1;
    }
    
    // Process data rows
    htmlTable += `  <tbody${options.bodyClass ? ` class="${options.bodyClass}"` : ''}>\n`;
    
    for (let i = startRow; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines if configured to do so
      if (options.skipEmptyLines && line === '') {
        continue;
      }
      
      const values = parseCSVLine(line, options.delimiter);
      
      if (values.length > 0) {
        // Apply striped row classes if enabled
        let rowClassValue = options.rowClass || '';
        if (options.stripedRows && (i - startRow) % 2 === 1) {
          rowClassValue += rowClassValue ? ' striped' : 'striped';
        }
        
        htmlTable += `    <tr${rowClassValue ? ` class="${rowClassValue}"` : ''}>\n`;
        
        for (const value of values) {
          const cellValue = options.trimValues ? value.trim() : value;
          htmlTable += `      <td${options.cellClass ? ` class="${options.cellClass}"` : ''}>${escapeHtml(cellValue)}</td>\n`;
        }
        
        htmlTable += '    </tr>\n';
      }
    }
    
    htmlTable += '  </tbody>\n';
    htmlTable += '</table>\n';
    
    if (options.responsiveTable) {
      htmlTable += '</div>';
    }
    
    return htmlTable;
  } catch (error) {
    throw error;
  }
};

// Helper function to escape HTML special characters
const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Handle escaped double quotes
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle the quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
};

// Function to generate CSS styles for HTML tables
const generateTableStyles = (
  options: {
    tableWidth: string;
    headerBgColor: string;
    headerTextColor: string;
    borderColor: string;
    stripedRowColor: string;
    hoverHighlight: boolean;
    fontSize: string;
    cellPadding: string;
    borderWidth: string;
    borderStyle: string;
  }
): string => {
  return `
<style>
  .csv-table {
    border-collapse: collapse;
    width: ${options.tableWidth};
    font-size: ${options.fontSize};
    border: ${options.borderWidth} ${options.borderStyle} ${options.borderColor};
  }
  
  .csv-table th {
    background-color: ${options.headerBgColor};
    color: ${options.headerTextColor};
    padding: ${options.cellPadding};
    text-align: left;
    border: ${options.borderWidth} ${options.borderStyle} ${options.borderColor};
  }
  
  .csv-table td {
    padding: ${options.cellPadding};
    border: ${options.borderWidth} ${options.borderStyle} ${options.borderColor};
  }
  
  .csv-table tbody tr.striped {
    background-color: ${options.stripedRowColor};
  }
  
  ${options.hoverHighlight ? `
  .csv-table tbody tr:hover {
    background-color: #f5f5f5;
  }
  ` : ''}
  
  .responsive-table {
    overflow-x: auto;
  }
</style>
`;
};

// Main component to convert CSV to HTML table
const CsvToHtml: React.FC<CsvToHtmlProps> = () => {
  // State for the CSV input
  const [csvInput, setCsvInput] = useState<string>('');
  // State for the HTML output
  const [htmlOutput, setHtmlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('table');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for showing style options
  const [showStyleOptions, setShowStyleOptions] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isHtmlGenerated, setIsHtmlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for preview
  const [showPreview, setShowPreview] = useState<boolean>(false);
  
  // Conversion options
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [tableClass, setTableClass] = useState<string>('csv-table');
  const [headerClass, setHeaderClass] = useState<string>('');
  const [bodyClass, setBodyClass] = useState<string>('');
  const [rowClass, setRowClass] = useState<string>('');
  const [cellClass, setCellClass] = useState<string>('');
  const [headerCellClass, setHeaderCellClass] = useState<string>('');
  const [stripedRows, setStripedRows] = useState<boolean>(true);
  const [responsiveTable, setResponsiveTable] = useState<boolean>(true);
  const [tableCaption, setTableCaption] = useState<string>('');
  const [includeStyles, setIncludeStyles] = useState<boolean>(true);
  
  // Style options
  const [tableWidth, setTableWidth] = useState<string>('100%');
  const [headerBgColor, setHeaderBgColor] = useState<string>('#f2f2f2');
  const [headerTextColor, setHeaderTextColor] = useState<string>('#333333');
  const [borderColor, setBorderColor] = useState<string>('#dddddd');
  const [stripedRowColor, setStripedRowColor] = useState<string>('#f9f9f9');
  const [hoverHighlight, setHoverHighlight] = useState<boolean>(true);
  const [fontSize, setFontSize] = useState<string>('14px');
  const [cellPadding, setCellPadding] = useState<string>('8px');
  const [borderWidth, setBorderWidth] = useState<string>('1px');
  const [borderStyle, setBorderStyle] = useState<string>('solid');
  
  // Handle CSV input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsvInput(e.target.value);
    setError('');
    setIsHtmlGenerated(false);
    setShowPreview(false);
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Update the file name based on the uploaded file
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop(); // Remove extension
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    // Read the file content
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setCsvInput(event.target.result as string);
        setError('');
        setIsHtmlGenerated(false);
        setShowPreview(false);
      }
    };
    reader.onerror = () => {
      setError('Error reading the file');
    };
    reader.readAsText(file);
  };

  // Use example data
  const useExampleData = () => {
    const exampleCsv = `Product,Category,Price,Stock,Rating
Basic T-Shirt,Clothing,$19.99,120,4.2
Premium Hoodie,Clothing,$49.99,65,4.8
Wireless Earbuds,Electronics,$89.99,42,4.5
Smart Watch,Electronics,$199.99,30,4.7
Running Shoes,Footwear,$79.99,80,4.3
Yoga Mat,Fitness,$29.99,55,4.6
Protein Powder,Nutrition,$39.99,100,4.4
Water Bottle,Accessories,$14.99,150,4.1`;

    setCsvInput(exampleCsv);
    setError('');
    setIsHtmlGenerated(false);
    setShowPreview(false);
    setHtmlOutput('');
  };

  // Convert CSV to HTML
  const convertCsvToHtml = () => {
    setError('');
    setIsLoading(true);
    setIsHtmlGenerated(false);
    setHtmlOutput('');
    setShowPreview(false);
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      // Generate the HTML table
      const options = {
        header: hasHeader,
        delimiter,
        trimValues,
        skipEmptyLines,
        tableClass,
        headerClass,
        bodyClass,
        rowClass,
        cellClass,
        headerCellClass,
        stripedRows,
        responsiveTable,
        caption: tableCaption
      };
      
      let htmlContent = csvToHtml(csvInput, options);
      
      // Add styles if included
      if (includeStyles) {
        const styleOptions = {
          tableWidth,
          headerBgColor,
          headerTextColor,
          borderColor,
          stripedRowColor,
          hoverHighlight,
          fontSize,
          cellPadding,
          borderWidth,
          borderStyle
        };
        
        htmlContent = generateTableStyles(styleOptions) + htmlContent;
      }
      
      setHtmlOutput(htmlContent);
      setIsHtmlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting CSV to HTML: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setCsvInput('');
    setHtmlOutput('');
    setError('');
    setFileName('table');
    setIsHtmlGenerated(false);
    setShowPreview(false);
  };

  // Copy HTML to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(htmlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download HTML file
  const downloadHtml = () => {
    const fullHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${fileName || 'CSV Table'}</title>
</head>
<body>
${htmlOutput}
</body>
</html>
    `;
    
    const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'table'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Toggle preview
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CSV to HTML Table Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert CSV (Comma-Separated Values) data to HTML table format. Upload a CSV file or paste your data below.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="csvInput" className="font-medium text-gray-700">
                CSV Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setCsvInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
            
            <textarea
              id="csvInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={csvInput}
              onChange={handleInputChange}
              placeholder="Paste your CSV data here, e.g.,
Product,Category,Price,Stock
T-Shirt,Clothing,$19.99,120
Headphones,Electronics,$89.99,42"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Output File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="tableCaption" className="block text-sm font-medium text-gray-700 mb-1">
                Table Caption (Optional)
              </label>
              <input
                type="text"
                id="tableCaption"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={tableCaption}
                onChange={(e) => setTableCaption(e.target.value)}
                placeholder="Enter a caption for your table"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasHeader" className="ml-2 block text-sm text-gray-700">
                    First row contains headers
                  </label>
                </div>
                
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trimValues"
                    checked={trimValues}
                    onChange={(e) => setTrimValues(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                    Trim whitespace from values
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipEmptyLines"
                    checked={skipEmptyLines}
                    onChange={(e) => setSkipEmptyLines(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                    Skip empty lines
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="responsiveTable"
                    checked={responsiveTable}
                    onChange={(e) => setResponsiveTable(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="responsiveTable" className="ml-2 block text-sm text-gray-700">
                    Make table responsive (horizontal scroll on small screens)
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="stripedRows"
                    checked={stripedRows}
                    onChange={(e) => setStripedRows(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="stripedRows" className="ml-2 block text-sm text-gray-700">
                    Use striped rows
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeStyles"
                    checked={includeStyles}
                    onChange={(e) => setIncludeStyles(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeStyles" className="ml-2 block text-sm text-gray-700">
                    Include CSS styles
                  </label>
                </div>
              </div>
            )}
            
            {includeStyles && (
              <div className="mb-4">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={() => setShowStyleOptions(!showStyleOptions)}
                >
                  <span>{showStyleOptions ? 'Hide' : 'Show'} Style Options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${showStyleOptions ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}
            
            {includeStyles && showStyleOptions && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="tableWidth" className="block text-sm font-medium text-gray-700 mb-1">
                    Table Width
                  </label>
                  <select
                    id="tableWidth"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={tableWidth}
                    onChange={(e) => setTableWidth(e.target.value)}
                  >
                    <option value="100%">Full Width (100%)</option>
                    <option value="75%">75% Width</option>
                    <option value="50%">50% Width</option>
                    <option value="auto">Auto Width</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="headerBgColor" className="block text-sm font-medium text-gray-700 mb-1">
                      Header Background Color
                    </label>
                    <div className="flex">
                      <input
                        type="color"
                        id="headerBgColor"
                        value={headerBgColor}
                        onChange={(e) => setHeaderBgColor(e.target.value)}
                        className="h-8 w-8 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={headerBgColor}
                        onChange={(e) => setHeaderBgColor(e.target.value)}
                        className="ml-2 flex-grow p-1 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="headerTextColor" className="block text-sm font-medium text-gray-700 mb-1">
                      Header Text Color
                    </label>
                    <div className="flex">
                      <input
                        type="color"
                        id="headerTextColor"
                        value={headerTextColor}
                        onChange={(e) => setHeaderTextColor(e.target.value)}
                        className="h-8 w-8 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={headerTextColor}
                        onChange={(e) => setHeaderTextColor(e.target.value)}
                        className="ml-2 flex-grow p-1 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="borderColor" className="block text-sm font-medium text-gray-700 mb-1">
                      Border Color
                    </label>
                    <div className="flex">
                      <input
                        type="color"
                        id="borderColor"
                        value={borderColor}
                        onChange={(e) => setBorderColor(e.target.value)}
                        className="h-8 w-8 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={borderColor}
                        onChange={(e) => setBorderColor(e.target.value)}
                        className="ml-2 flex-grow p-1 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="stripedRowColor" className="block text-sm font-medium text-gray-700 mb-1">
                      Striped Row Color
                    </label>
                    <div className="flex">
                      <input
                        type="color"
                        id="stripedRowColor"
                        value={stripedRowColor}
                        onChange={(e) => setStripedRowColor(e.target.value)}
                        className="h-8 w-8 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={stripedRowColor}
                        onChange={(e) => setStripedRowColor(e.target.value)}
                        className="ml-2 flex-grow p-1 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fontSize" className="block text-sm font-medium text-gray-700 mb-1">
                      Font Size
                    </label>
                    <select
                      id="fontSize"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={fontSize}
                      onChange={(e) => setFontSize(e.target.value)}
                    >
                      <option value="12px">Small (12px)</option>
                      <option value="14px">Medium (14px)</option>
                      <option value="16px">Large (16px)</option>
                      <option value="18px">Extra Large (18px)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="cellPadding" className="block text-sm font-medium text-gray-700 mb-1">
                      Cell Padding
                    </label>
                    <select
                      id="cellPadding"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={cellPadding}
                      onChange={(e) => setCellPadding(e.target.value)}
                    >
                      <option value="4px">Compact (4px)</option>
                      <option value="8px">Standard (8px)</option>
                      <option value="12px">Comfortable (12px)</option>
                      <option value="16px">Spacious (16px)</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="borderWidth" className="block text-sm font-medium text-gray-700 mb-1">
                      Border Width
                    </label>
                    <select
                      id="borderWidth"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={borderWidth}
                      onChange={(e) => setBorderWidth(e.target.value)}
                    >
                      <option value="0px">None (0px)</option>
                      <option value="1px">Thin (1px)</option>
                      <option value="2px">Medium (2px)</option>
                      <option value="3px">Thick (3px)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="borderStyle" className="block text-sm font-medium text-gray-700 mb-1">
                      Border Style
                    </label>
                    <select
                      id="borderStyle"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={borderStyle}
                      onChange={(e) => setBorderStyle(e.target.value)}
                    >
                      <option value="solid">Solid</option>
                      <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                      <option value="double">Double</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hoverHighlight"
                    checked={hoverHighlight}
                    onChange={(e) => setHoverHighlight(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hoverHighlight" className="ml-2 block text-sm text-gray-700">
                    Highlight rows on hover
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertCsvToHtml}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to HTML'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              HTML Output
            </label>
            
            {isHtmlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy HTML'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadHtml}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={togglePreview}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isHtmlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    HTML output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isHtmlGenerated && !showPreview && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="htmlOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={htmlOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
            
            {!error && isHtmlGenerated && showPreview && (
              <div className="flex-grow flex flex-col border border-gray-300 rounded-md p-3 bg-white overflow-auto">
                <div
                  className="preview-container"
                  dangerouslySetInnerHTML={{ __html: htmlOutput }}
                />
              </div>
            )}
          </div>
          
          {isHtmlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">CSV Successfully Converted to HTML!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated HTML table for use in your website or document.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About CSV to HTML Table Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool parses CSV (Comma-Separated Values) data and converts it into an HTML table format. CSV files are plain text files that store tabular data with each value separated by a delimiter (typically a comma). The converter generates clean, properly structured HTML tables that you can easily embed in your web pages or documents.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
              <li><strong>Delimiter Support</strong> - Handle various delimiters (comma, semicolon, tab, pipe)</li>
              <li><strong>Table Headers</strong> - Option to use first row as headers or generate default column headers</li>
              <li><strong>Responsive Tables</strong> - Generate tables that work well on mobile and desktop devices</li>
              <li><strong>Style Customization</strong> - Full control over table styling including colors, borders, and spacing</li>
              <li><strong>Striped Rows</strong> - Alternate row colors for better readability</li>
              <li><strong>Hover Highlights</strong> - Interactive row highlighting on mouse hover</li>
              <li><strong>Live Preview</strong> - See how your table will look before downloading</li>
              <li><strong>Quoted Value Support</strong> - Properly handles quoted fields in CSV</li>
              <li><strong>HTML Download</strong> - Save your table as a complete HTML file</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Common Use Cases</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Creating data tables for websites and blogs</li>
              <li>Converting spreadsheet data into web-friendly format</li>
              <li>Adding product listings to e-commerce sites</li>
              <li>Creating pricing tables for services</li>
              <li>Converting report data for web publication</li>
              <li>Sharing data tables via email or HTML newsletters</li>
              <li>Adding structured data to content management systems</li>
              <li>Creating responsive tables for mobile-friendly websites</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">CSV Format Best Practices</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use consistent delimiter throughout the file</li>
              <li>Include a header row for better table structure</li>
              <li>Use double quotes around values that contain delimiters or newlines</li>
              <li>Escape double quotes within quoted fields by doubling them (e.g., "He said ""Hello"" to me")</li>
              <li>Keep column count consistent across all rows</li>
              <li>Avoid special characters in headers for better compatibility</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">HTML Table Accessibility Tips</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Always include proper headers using <code>&lt;th&gt;</code> elements</li>
              <li>Add a descriptive <code>&lt;caption&gt;</code> to explain the table's purpose</li>
              <li>Maintain sufficient color contrast for text readability</li>
              <li>Ensure the table is responsive for mobile devices</li>
              <li>Consider adding <code>scope="col"</code> or <code>scope="row"</code> attributes to header cells</li>
              <li>For complex tables, use <code>id</code> and <code>headers</code> attributes to associate data cells with headers</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvToHtml;